import { ResultOf } from "@graphql-typed-document-node/core";
import { AuthConfig, AuthUtilities, authExchange } from "@urql/exchange-auth";
import { cacheExchange } from "@urql/exchange-graphcache";
import { retryExchange } from "@urql/exchange-retry";
import { User } from "firebase/auth";
import { useMemo } from "react";
import { CombinedError, Client as UrqlClient, fetchExchange, mapExchange } from "urql";

import { graphql } from "@/gen";
import {
    CreateGameMutationVariables,
    CreateRoundMutationVariables,
    CreateTournamentStageMutationVariables,
} from "@/gen/graphql";
import { useAuth } from "@/hooks/useAuth";
import { backendUrl } from "@/utils/backend";
import { hasGraphqlError } from "@/utils/graphql-errors";

import schema from "../../graphql.schema.json";

const createAuthExchangeInit = (user: User | null) => {
    return async (utils: AuthUtilities): Promise<AuthConfig> => {
        let idToken = user ? await user.getIdToken() : undefined;
        return {
            addAuthToOperation: operation => {
                if (idToken === undefined) {
                    return operation;
                }

                // LOCAL DEV: Use fake Firebase header for local bypass
                if (
                    process.env.NODE_ENV === "development" &&
                    idToken === "fake-token-for-local-dev"
                ) {
                    return utils.appendHeaders(operation, {
                        "x-fake-firebase-user-id": "q1wjimsABmd2nmdtOX28F43FAAB3",
                    });
                }

                return utils.appendHeaders(operation, {
                    Authorization: `Bearer ${idToken}`,
                });
            },
            didAuthError(error: CombinedError) {
                // LOCAL DEV: Don't treat as auth error for fake token
                if (
                    process.env.NODE_ENV === "development" &&
                    idToken === "fake-token-for-local-dev"
                ) {
                    return false;
                }

                if (error?.response === undefined) {
                    return false;
                }

                return hasGraphqlError(error, "UNAUTHORIZED") || error.response.status === 401;
            },
            async refreshAuth() {
                // LOCAL DEV: Don't try to refresh fake token
                if (
                    process.env.NODE_ENV === "development" &&
                    idToken === "fake-token-for-local-dev"
                ) {
                    return;
                }

                if (!user) {
                    throw new Error("No user available");
                }

                idToken = await user.getIdToken(true);

                if (idToken === undefined) {
                    throw new Error("No ID token available");
                }
            },
        };
    };
};

export const useGraphQLClient = () => {
    const { authUser } = useAuth();

    return useMemo(() => {
        return new UrqlClient({
            url: `${backendUrl()}/internal-graphql`,
            exchanges: [
                cacheExchange({
                    schema,
                    keys: {
                        SearchPlayersResponse: () => null,
                        TrialsResponse: () => null,
                        ExternalUrlArticle: () => null,
                        RichTextArticle: () => null,
                        TournamentTag: () => null,
                        PlayerTag: () => null,
                    },
                    updates: {
                        Mutation: {
                            deleteLayout: (
                                _result,
                                args: { input: { layoutId: string } },
                                cache
                            ) => {
                                cache.invalidate({
                                    __typename: "Layout",
                                    id: args.input?.layoutId,
                                });
                            },
                            sendArticlePushNotification: (_result, args, cache) => {
                                cache.invalidate({
                                    __typename: "Article",
                                    id: args.id as string,
                                });
                            },
                            updateRichTextArticle: (_result, args, cache) => {
                                cache.invalidate({
                                    __typename: "Article",
                                    id: args.id as string,
                                });
                            },
                            createTournamentStage(
                                result,
                                args: CreateTournamentStageMutationVariables,
                                cache
                            ) {
                                const tournamentFragment = graphql(`
                                    fragment _ on Tournament {
                                        __typename
                                        id

                                        stages {
                                            __typename
                                            id
                                        }
                                    }
                                `);
                                type TournamentFragment = ResultOf<typeof tournamentFragment>;
                                const tournament = cache.readFragment<TournamentFragment>(
                                    tournamentFragment,
                                    {
                                        id: args.input.tournamentId,
                                    } as TournamentFragment
                                );
                                if (tournament != null) {
                                    cache.writeFragment(tournamentFragment, {
                                        ...tournament,
                                        stages: [
                                            ...tournament.stages,
                                            result.createTournamentStage,
                                        ],
                                    });
                                }
                            },
                            createRound(result, args: CreateRoundMutationVariables, cache) {
                                const tournamentStageFragment = graphql(`
                                    fragment _TournamentStage on TournamentStage {
                                        __typename
                                        id

                                        rounds {
                                            __typename
                                            id
                                        }
                                    }
                                `);
                                type TournamentStageFragment = ResultOf<
                                    typeof tournamentStageFragment
                                >;
                                const tournamentStage = cache.readFragment<TournamentStageFragment>(
                                    tournamentStageFragment,
                                    {
                                        id: args.input.tournamentStageId,
                                    } as TournamentStageFragment
                                );
                                if (tournamentStage != null) {
                                    cache.writeFragment(tournamentStageFragment, {
                                        ...tournamentStage,
                                        rounds: [...tournamentStage.rounds, result.createRound],
                                    });
                                }
                            },
                            createGame(result, args: CreateGameMutationVariables, cache) {
                                const trialFragment = graphql(`
                                    fragment _Trial on Trial {
                                        __typename
                                        id

                                        games {
                                            __typename
                                            id
                                        }
                                    }
                                `);
                                type TrialFragment = ResultOf<typeof trialFragment>;
                                const trial = cache.readFragment<TrialFragment>(trialFragment, {
                                    id: args.input.trialId,
                                } as TrialFragment);
                                if (trial != null) {
                                    cache.writeFragment(trialFragment, {
                                        ...trial,
                                        games: [...trial.games, result.createGame],
                                    });
                                }
                            },
                        },
                    },
                }),
                mapExchange({
                    onResult: result => {
                        if (result.error) {
                            // prevent partially overwriting cache on errors
                            // https://github.com/urql-graphql/urql/issues/825
                            result.data = null;
                        }
                        return result;
                    },
                }),
                authExchange(createAuthExchangeInit(authUser)),
                retryExchange({
                    initialDelayMs: 500,
                    maxNumberAttempts: 3,
                    retryIf: (err, operation) =>
                        hasGraphqlError(err, "UNAUTHORIZED") ||
                        (operation.kind === "query" && Boolean(err)),
                }),
                fetchExchange,
            ],
            requestPolicy: "cache-and-network",
            fetchOptions: {
                credentials: "include",
            },
        });
    }, [authUser]);
};
